import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

export default function ExercicesPage() {
  const [exercices, setExercices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ annee: new Date().getFullYear() + 1, debut: '', fin: '' });
  const [actionLoading, setActionLoading] = useState(null);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try { const r = await api.get('/syscohada/exercices'); setExercices(r.data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/syscohada/exercices', form);
      setShowForm(false);
      load();
    } catch {}
  };

  const handleCloturer = async (id) => {
    if (!window.confirm('Clôturer cet exercice ? Les écritures ne pourront plus être ajoutées sur cette période.')) return;
    setActionLoading(id);
    try { await api.put(`/syscohada/exercices/${id}/cloturer`); load(); } catch {} finally { setActionLoading(null); }
  };

  const handleRouvrir = async (id) => {
    if (!window.confirm('Rouvrir cet exercice ? Cela permettra à nouveau les écritures sur cette période.')) return;
    setActionLoading(id);
    try { await api.put(`/syscohada/exercices/${id}/rouvrir`); load(); } catch {} finally { setActionLoading(null); }
  };

  const actif = exercices.find(e => e.statut === 'ouvert');
  const clotures = exercices.filter(e => e.statut === 'cloture').length;

  const STATUS_STYLES = {
    ouvert: { bg: '#ECFDF5', color: '#059669', label: 'Ouvert' },
    cloture: { bg: '#FEF3C7', color: '#D97706', label: 'Clôturé' },
    archive: { bg: '#F3F4F6', color: '#6B7280', label: 'Archivé' },
  };

  return (
    <div>
      <div className="stats-grid stats-grid--3">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Exercices</span></div>
          <div className="stat-card-value">{exercices.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Exercice actif</span></div>
          <div className="stat-card-value" style={{ fontSize: 18 }}>{actif ? actif.annee : '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Clôturés</span></div>
          <div className="stat-card-value">{clotures}</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Exercices comptables</div>
          <button className="gss-btn gss-btn-primary" onClick={() => {
            const nextYear = (actif?.annee || new Date().getFullYear()) + 1;
            setForm({ annee: nextYear, debut: `${nextYear}-01-01`, fin: `${nextYear}-12-31` });
            setShowForm(true);
          }}>+ Nouvel exercice</button>
        </div>

        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : exercices.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucun exercice comptable</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Année</th>
                  <th>Début</th>
                  <th>Fin</th>
                  <th>Statut</th>
                  <th>Date clôture</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exercices.map(ex => {
                  const st = STATUS_STYLES[ex.statut] || STATUS_STYLES.archive;
                  return (
                    <tr key={ex.id}>
                      <td style={{ fontWeight: 700, fontSize: 16 }}>{ex.annee}</td>
                      <td>{ex.date_debut?.split('T')[0]}</td>
                      <td>{ex.date_fin?.split('T')[0]}</td>
                      <td>
                        <span className="gss-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                      </td>
                      <td style={{ color: '#888' }}>{ex.date_cloture ? ex.date_cloture.split('T')[0] : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {ex.statut === 'ouvert' && (
                          <button className="gss-btn" style={{ fontSize: 12, padding: '4px 12px', background: '#D97706', color: '#fff', border: 'none', borderRadius: 4 }}
                            onClick={() => handleCloturer(ex.id)} disabled={actionLoading === ex.id}>
                            {actionLoading === ex.id ? '...' : 'Clôturer'}
                          </button>
                        )}
                        {ex.statut === 'cloture' && (
                          <button className="gss-btn" style={{ fontSize: 12, padding: '4px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4 }}
                            onClick={() => handleRouvrir(ex.id)} disabled={actionLoading === ex.id}>
                            {actionLoading === ex.id ? '...' : 'Rouvrir'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header"><h2>Nouvel exercice</h2><button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button></div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Année *</label><input type="number" className="gss-input" required value={form.annee} onChange={e => setForm({ ...form, annee: Number(e.target.value) })} /></div>
                  <div className="gss-field"><label>Date début</label><input type="date" className="gss-input" value={form.debut} onChange={e => setForm({ ...form, debut: e.target.value })} /></div>
                  <div className="gss-field"><label>Date fin</label><input type="date" className="gss-input" value={form.fin} onChange={e => setForm({ ...form, fin: e.target.value })} /></div>
                </div>
              </div>
              <div className="gss-modal-actions"><button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button><button type="submit" className="gss-btn-deploy">Créer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
